import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useTokenUsageSummary, useTokenUsageByAgent } from '@/hooks/useTokenUsage'
import type { AgentTokenSummary } from '@/lib/api'

function formatCost(usd: number): string {
  if (usd < 0.01 && usd > 0) return '<$0.01'
  return `$${usd.toFixed(2)}`
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function successBadge(rate: number) {
  if (rate >= 80) return <Badge variant="success">{rate}%</Badge>
  if (rate >= 50) return <Badge variant="default">{rate}%</Badge>
  return <Badge variant="destructive">{rate}%</Badge>
}

function AgentDetailPanel({ agentName }: { agentName: string }) {
  const { data, isLoading } = useTokenUsageByAgent(agentName)

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>HOURLY BREAKDOWN — {agentName.toUpperCase()}</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-40 w-full" /></CardContent>
      </Card>
    )
  }

  if (!data || data.buckets.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>HOURLY BREAKDOWN — {agentName.toUpperCase()}</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-cmd-outline font-label">NO DATA FOR LAST 48H</p></CardContent>
      </Card>
    )
  }

  const maxTokens = Math.max(...data.buckets.map((b) => b.total_tokens), 1)

  return (
    <Card>
      <CardHeader><CardTitle>HOURLY BREAKDOWN — {agentName.toUpperCase()}</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-1">
          {data.buckets.map((bucket) => {
            const pct = (bucket.total_tokens / maxTokens) * 100
            const hour = new Date(bucket.hour).toLocaleString('en-GB', {
              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
            })
            return (
              <div key={bucket.hour} className="flex items-center gap-2 text-xs">
                <span className="w-28 text-right font-mono text-cmd-outline shrink-0">{hour}</span>
                <div className="flex-1 flex h-2.5 bg-cmd-low rounded-sm overflow-hidden">
                  <div
                    className="bg-primary h-full"
                    style={{ width: `${pct * (bucket.successes / Math.max(bucket.runs, 1))}%` }}
                  />
                  <div
                    className="bg-cmd-error h-full"
                    style={{ width: `${pct * (bucket.failures / Math.max(bucket.runs, 1))}%` }}
                  />
                </div>
                <span className="w-14 text-right font-mono text-cmd-outline tabular-nums shrink-0">
                  {formatTokens(bucket.total_tokens)}
                </span>
                <span className="w-14 text-right font-mono text-cmd-outline tabular-nums shrink-0">
                  {formatCost(bucket.cost_usd)}
                </span>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

export function CostsPage() {
  const { data, isLoading } = useTokenUsageSummary()
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null)

  return (
    <div className="space-y-6">
      {/* Cost totals */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader><CardTitle>COST TODAY</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-10 w-24" /> : (
              <p className="font-mono text-[2rem] font-medium text-white tabular-nums">
                {formatCost(data?.total_cost_24h_usd ?? 0)}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>COST THIS WEEK</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-10 w-24" /> : (
              <p className="font-mono text-[2rem] font-medium text-white tabular-nums">
                {formatCost(data?.total_cost_7d_usd ?? 0)}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>COST THIS MONTH</CardTitle></CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-10 w-24" /> : (
              <p className="font-mono text-[2rem] font-medium text-white tabular-nums">
                {formatCost(data?.total_cost_30d_usd ?? 0)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Per-agent breakdown table */}
      <Card>
        <CardHeader><CardTitle>PER-AGENT BREAKDOWN</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : data && data.agents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cmd-highest text-cmd-outline font-label text-[0.65rem] uppercase">
                    <th className="text-left py-2 pr-4">Agent</th>
                    <th className="text-left py-2 pr-4">Model</th>
                    <th className="text-right py-2 pr-4">Tokens (24h)</th>
                    <th className="text-right py-2 pr-4">Cost (24h)</th>
                    <th className="text-right py-2 pr-4">Tokens (7d)</th>
                    <th className="text-right py-2 pr-4">Cost (7d)</th>
                    <th className="text-right py-2 pr-4">Runs</th>
                    <th className="text-right py-2">Success</th>
                  </tr>
                </thead>
                <tbody>
                  {data.agents.map((agent, i) => (
                    <AgentRow
                      key={agent.name}
                      agent={agent}
                      index={i}
                      selected={selectedAgent === agent.name}
                      onSelect={() => setSelectedAgent(
                        selectedAgent === agent.name ? null : agent.name,
                      )}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-cmd-outline font-label">NO TOKEN USAGE RECORDED</p>
          )}
        </CardContent>
      </Card>

      {/* Agent detail panel */}
      {selectedAgent && <AgentDetailPanel agentName={selectedAgent} />}
    </div>
  )
}

function AgentRow({
  agent, index, selected, onSelect,
}: {
  agent: AgentTokenSummary; index: number; selected: boolean; onSelect: () => void
}) {
  const modelShort = agent.model.includes('sonnet') ? 'Sonnet 4.6' :
    agent.model.includes('haiku') ? 'Haiku 4.5' : agent.model

  return (
    <tr
      className={`border-b border-cmd-highest/50 cursor-pointer transition-colors hover:bg-cmd-highest/30 ${
        selected ? 'bg-cmd-highest/40' : index % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'
      }`}
      onClick={onSelect}
    >
      <td className="py-2 pr-4 font-mono text-xs text-primary uppercase">{agent.name}</td>
      <td className="py-2 pr-4 font-label text-xs text-cmd-outline">{modelShort}</td>
      <td className="py-2 pr-4 text-right font-mono text-xs text-white/80 tabular-nums">
        {formatTokens(agent.tokens_24h)}
      </td>
      <td className="py-2 pr-4 text-right font-mono text-xs text-white/80 tabular-nums">
        {formatCost(agent.cost_24h_usd)}
      </td>
      <td className="py-2 pr-4 text-right font-mono text-xs text-white/80 tabular-nums">
        {formatTokens(agent.tokens_7d)}
      </td>
      <td className="py-2 pr-4 text-right font-mono text-xs text-white/80 tabular-nums">
        {formatCost(agent.cost_7d_usd)}
      </td>
      <td className="py-2 pr-4 text-right font-mono text-xs text-cmd-outline tabular-nums">
        {agent.total_runs}
      </td>
      <td className="py-2 text-right">{successBadge(agent.success_rate)}</td>
    </tr>
  )
}
